/**
 * Utility class for building xpath queries. Builder provides
 * methods that allow formation of individual attribute queries
 * as well as complex xpath queries.
 *
 * @param { String } xpath - (optional) an xpath to initialize the builder with
 *
 * ```js
 * const xpath = builder.mdBase().match(attribute, "star wars")
 * // /FullMetadata/BaseObjectMetadata/ResourceName "star" AND etc...
 *
 * const xpath = builder.mdBase().is(attribute, "plane")
 * // /FullMetadata/BaseObjectMetadata/ResourceName IS "plane"
 *
 * const xpath = builder.or(q1,q2).wrap().and(q3,q4).wrap(ASCII)
 * // ASCII((<q1> OR <q2>) AND <q3> AND <q4>)
 *
 *
 * ```
 */
export default class XpathBuilder {
  constructor(xpath = "") {
    this.xpath = xpath;
  }

  join = (conjunction, ...expressions) => expressions.join(conjunction);

  not() {
    this.xpath = `NOT ${this.xpath}`;
    return this;
  }

  or(...expressions) {
    this.xpath = this.join(`${" "}OR${" "}`, ...expressions);
    return this;
  }

  and(...expressions) {
    this.xpath = this.join(`${" "}AND${" "}`, ...expressions);
    return this;
  }

  wrap(prefix = "") {
    this.xpath = `${prefix}(${this.xpath})`;
    return this;
  }

  is(attribute, value) {
    this.xpath = `${this.xpath}/${attribute}${" "}IS${" "}"${value}"`;
    return this;
  }

  lte(attribute, value) {
    this.xpath = `${this.xpath}/${attribute}${" "}<=${" "}${value}`;
    return this;
  }

  gte(attribute, value) {
    this.xpath = `${this.xpath}/${attribute}${" "}>=${" "}${value}`;
    return this;
  }

  append(attribute) {
    this.xpath = `${this.xpath}/${attribute}`;
    return this;
  }

  multiMatch(attribute, value, attProp) {
    const words = value.split(" ");
    const expressions = words.map(
      (word) =>
        `${this.xpath}/${attribute}${
          attProp ? `@${attProp}` : ""
        }${" "}"${word}"`
    );

    this.xpath = "";
    return this.and(...expressions);
  }

  match(attribute, value, attProp) {
    this.xpath = `${this.xpath}/${attribute}${
      attProp ? `@${attProp}` : ""
    }${" "}"${value}"`;
    return this;
  }

  exists() {
    this.xpath += " EXISTS";
    return this;
  }

  md(mdType1, mdType2) {
    switch (mdType1) {
      case "full":
        this.xpath = "/FullMetadata";
        break;
      case "prov":
        this.xpath = "/ProvenanceMetadata";
        break;
      default:
        throw new Error(`mdType1 provided is invalid: ${mdType1}`);
    }

    if (mdType2) {
      switch (mdType2) {
        case "base":
          this.xpath = `${this.xpath}/BaseObjectData`;
          break;
        case "extra":
          this.xpath = `${this.xpath}/ExtraObjectMetadata`;
          break;
        default:
          throw new Error(`mdType1 provided is invalid: ${mdType2}`);
      }
    }

    return this;
  }

  fullBase() {
    return this.md("full", "base");
  }

  fullExtra() {
    return this.md("full", "extra");
  }

  clear() {
    const { xpath } = this;
    this.xpath = "";
    return xpath;
  }

  set(xpath) {
    this.xpath = xpath;
    return this;
  }

  // potential future methods
  // path(...paths) {
  // this.join("/", paths)
  // }
  // isnot = ()=> "ISNOT"
  // union = () => "|"
  // plus = () => "+"
  // minus = () => "-"
  // times = () => "*"
  // dice = () => "div"
  // equal = () => "="
  // not = () => "!="
  // lt = () => "<"
  // lte = () => "<="
  // gt = () => ">"
  // gte = () => ">="
  // mod = () => "mod"
}
